/**
 * La file de validation parentale (`web-pwa` §6.6, `exercise` §11).
 *
 * Ce que le parent voit d'un travail : **l'énoncé, le corrigé, la photo et une
 * grille de trois à six points**. Le corrigé y est délibérément — §11.2 dit
 * qu'« un parent ne maîtrise pas la spécialité physique-chimie de terminale »,
 * et lui demander de juger un schéma de forces sans lui, c'est lui demander de
 * deviner.
 *
 * Sur une grille générée, le parent ne dit **pas** « juste » ou « faux ». Il
 * coche ce qu'il voit, et le serveur en tire le verdict. La nuance est toute la
 * §11.2 — « le parent valide ce qu'il peut voir, jamais ce qu'il devrait
 * savoir ».
 *
 * **L'exception est un travail sans liste de lignes** (`mode === "verdict"`) :
 * un sujet à images, une composition dont la grille a des critères et des
 * niveaux. Hedi, le 26 septembre 2026 : *« Je veux pouvoir la corriger en tant
 * que parent et renseigner si c'est correct ou pas. »* Là, deux boutons, parce
 * qu'il n'y a rien à cocher : le parent lit la copie à côté du corrigé, les
 * critères sont affichés **en lecture seule** pour guider cette lecture, et une
 * seule question est posée. Le mode vient du serveur, jamais de l'écran.
 *
 * **Cet écran ne photographie plus.** Sa version précédente le faisait, avec
 * une grille de trois points écrite en dur, parce que la file n'existait pas
 * côté serveur et qu'il fallait bien montrer quelque chose : c'était l'enfant
 * qui fait le travail, donc c'est l'enfant qui le photographie
 * (`RenduAuParent`). Un parent qui photographie la copie de son enfant fait
 * l'exercice avec lui, ce qui est précisément ce qu'on mesure.
 *
 * Le lien profond de Telegram arrive ici avec l'identifiant de l'exercice, et
 * cet exercice est **remonté en tête** plutôt qu'isolé : un parent qui vient
 * pour un travail et en a deux en attente doit voir le second.
 *
 * **La copie peut arriver pré-corrigée** (`core-api` §3.28, `web-pwa` §17.9) :
 * un modèle a lu la photo avec le corrigé et propose un verdict par point. Les
 * points proposés « juste » arrivent **cochés**, la phrase du tuteur sous
 * chacun ; « correct / pas correct » arrive pré-armé. Rien n'est envoyé sans le
 * bouton du parent, et c'est son envoi — pas la proposition — qui écrit dans
 * `mastery` (règle 3).
 */

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { call } from "@/api/client";
import type { PropositionDeCopieOut, ValidationADemander } from "@/api/schema";
import { LiensDImpression } from "@/ui/impression";
import { TexteMathematique } from "@/ui/TexteMathematique";
import { Button, Card, Empty, ErrorNote, Screen } from "@/ui/primitives";

/**
 * L'ordre d'affichage : l'exercice du lien d'abord, puis les plus anciens.
 *
 * Pur, et exporté pour cette raison — c'est la seule règle de cet écran qui
 * puisse se tromper sans qu'on le voie à l'œil.
 */
export function ordonner(
  demandes: ValidationADemander[],
  exerciceVise: string | undefined,
): ValidationADemander[] {
  if (!exerciceVise) return demandes;
  const vise = demandes.filter((demande) => demande.exercise_id === exerciceVise);
  return [...vise, ...demandes.filter((demande) => demande.exercise_id !== exerciceVise)];
}

/** Ce que dit le bouton, selon ce qui reste à cocher. */
export function libelleDuBouton(coches: boolean[], total: number): string {
  const restant = total - coches.filter(Boolean).length;
  if (total === 0) return "Rien à cocher";
  if (restant === 0) return "Valider";
  return `Encore ${restant} point${restant > 1 ? "s" : ""}`;
}

/**
 * Le bouton d'un exercice **marqué pour le parent** (énoncé mal décodé,
 * corrigé du professeur…). Ici chaque ligne cochée veut dire « cette réponse
 * est juste », et une ligne non cochée « fausse » : la réussite se compte en
 * part des réponses justes (décision du 26 septembre 2026), donc le bouton est
 * toujours actif et dit ce qu'il enverra.
 */
export function libelleDuBoutonMarque(coches: boolean[]): string {
  const justes = coches.filter(Boolean).length;
  return `Envoyer : ${justes} sur ${coches.length} juste${justes > 1 ? "s" : ""}`;
}

/** Vrai quand la demande porte un motif de validation parentale. */
export function estMarquee(demande: ValidationADemander): boolean {
  return Boolean(demande.motif);
}

/**
 * Vrai quand la demande se juge d'un mot, correct ou pas correct. Lu dans
 * `mode`, que le serveur décide : une seconde règle écrite ici finirait par
 * offrir deux boutons sur une grille qui se coche.
 */
export function seJugeDUnMot(demande: ValidationADemander): boolean {
  return demande.mode === "verdict";
}

/**
 * Les propositions du tuteur, par cible : le rang du point (`"0"`, `"1"`…) ou
 * `"copie"` pour un travail jugé d'un mot. Le générateur de types rend un
 * dictionnaire en `Record<string, unknown>` ; la forme est celle du schéma
 * `PropositionDeCopieOut`.
 */
export function propositionsDe(
  demande: ValidationADemander,
): Record<string, PropositionDeCopieOut> {
  return (demande.propositions ?? {}) as Record<string, PropositionDeCopieOut>;
}

/** Les cases pré-cochées : un point proposé « juste », et lui seul. Un point
 *  « illisible » ou sans proposition arrive vide, comme avant. */
export function cochesProposees(demande: ValidationADemander): boolean[] {
  const propositions = propositionsDe(demande);
  return demande.checklist.map((_, rang) => propositions[String(rang)]?.verdict === "juste");
}

/** Le verdict d'un mot pré-armé : « juste » → correct, « faux » → pas correct,
 *  et rien pour le reste — « à moitié » ne se range dans aucun des deux mots,
 *  c'est au parent de trancher. */
export function verdictDUnMotPropose(demande: ValidationADemander): boolean | null {
  const verdict = propositionsDe(demande).copie?.verdict;
  if (verdict === "juste") return true;
  if (verdict === "faux") return false;
  return null;
}

const DIT_PAR_LE_TUTEUR: Record<string, string> = {
  juste: "juste",
  moitie: "à moitié juste",
  faux: "faux",
  pas_fait: "pas fait",
  illisible: "illisible — à relire vous-même",
};

function PhraseDuTuteur({ proposition }: { proposition: PropositionDeCopieOut | undefined }) {
  if (!proposition) return null;
  return (
    <p className="muted small">
      Proposition du tuteur :{" "}
      <strong>{DIT_PAR_LE_TUTEUR[proposition.verdict] ?? proposition.verdict}</strong>
      {proposition.justification ? ` — ${proposition.justification}` : ""}
    </p>
  );
}

/** La phrase de confirmation du verdict. Pure, exportée pour être testée. */
export function libelleDeConfirmation(correct: boolean, prenom: string): string {
  return `Noter le travail de ${prenom} « ${correct ? "correct" : "pas correct"} » ? Cela ne se reprend pas.`;
}

export function ValidationScreen() {
  const { exerciseId } = useParams<{ exerciseId?: string }>();
  const [demandes, setDemandes] = useState<ValidationADemander[] | null>(null);
  const [panne, setPanne] = useState(false);

  const charger = useCallback(async () => {
    setPanne(false);
    try {
      const reponse = await call("fileDeValidation");
      setDemandes(reponse.demandes ?? []);
    } catch {
      setPanne(true);
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  if (panne) {
    return (
      <Screen title="À valider">
        <ErrorNote>La file n'a pas pu être lue. Réessayez dans un instant.</ErrorNote>
      </Screen>
    );
  }

  if (demandes === null) {
    return (
      <Screen title="À valider">
        <p className="muted">Chargement…</p>
      </Screen>
    );
  }

  if (demandes.length === 0) {
    return (
      <Screen title="À valider" lead="Les exercices que le tuteur ne peut pas corriger seul.">
        <Empty>Rien n'attend votre relecture.</Empty>
      </Screen>
    );
  }

  return (
    <Screen title="À valider" lead="Les exercices que le tuteur ne peut pas corriger seul.">
      {ordonner(demandes, exerciseId).map((demande) => (
        <Demande key={demande.id} demande={demande} fait={charger} />
      ))}
    </Screen>
  );
}

function Demande({ demande, fait }: { demande: ValidationADemander; fait: () => void }) {
  const [coches, setCoches] = useState<boolean[]>(() => cochesProposees(demande));
  const propositions = propositionsDe(demande);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  // Le verdict choisi, en attente de confirmation ; `null` tant que rien.
  const [arme, setArme] = useState<boolean | null>(() => verdictDUnMotPropose(demande));

  const juger = async (correct: boolean) => {
    setEnvoi(true);
    setErreur(null);
    try {
      await call("juger", { params: { request_id: demande.id }, body: { correct } });
      fait();
    } catch {
      setErreur("Le verdict n'est pas parti. Réessayez dans un instant.");
      setEnvoi(false);
      setArme(null);
    }
  };

  const valider = async () => {
    setEnvoi(true);
    setErreur(null);
    try {
      await call("valider", { params: { request_id: demande.id }, body: { checked: coches } });
      fait();
    } catch {
      setErreur("La validation n'est pas partie. Réessayez dans un instant.");
      setEnvoi(false);
    }
  };

  const marquee = estMarquee(demande);
  const dUnMot = seJugeDUnMot(demande);
  // Les pages de l'ouvrage sont montrées sur un travail marqué et sur tout ce
  // qui se juge d'un mot : un sujet à images n'a souvent pas d'autre énoncé.
  const pages = marquee || dUnMot;
  const complete = coches.length > 0 && coches.every(Boolean);
  // Une demande marquée s'envoie à tout moment : les cases non cochées y sont
  // des réponses fausses, pas des points non regardés.
  const envoyable = marquee ? coches.length > 0 : complete;
  const enonces = demande.statement_image_urls ?? [];
  const corriges = demande.solution_image_urls ?? [];

  return (
    <Card title={`${demande.child_name}${demande.subject ? ` — ${demande.subject}` : ""}`}>
      {pages ? (
        <>
          {/* Le texte décodé de ces exercices n'est pas fiable : ni l'énoncé ni
              le corrigé ne sont montrés en texte, seulement les pages de
              l'ouvrage, présignées pour une courte durée (règle 8). */}
          {demande.motif_phrase ? <p className="muted">{demande.motif_phrase}</p> : null}
          {marquee ? (
            <p>{demande.statement}</p>
          ) : (
            <TexteMathematique>{demande.statement}</TexteMathematique>
          )}
          {enonces.map((url, rang) => (
            <img
              key={url}
              className="validation__photo"
              src={url}
              alt={`Énoncé, page ${rang + 1}`}
            />
          ))}
        </>
      ) : (
        <TexteMathematique>{demande.statement}</TexteMathematique>
      )}

      {/* La photo du travail. Elle n'est jamais partie dans Telegram (§11.3) :
          le message portait un lien, et le lien mène ici, dans la session où le
          parent est déjà authentifié. L'URL est présignée pour quinze minutes. */}
      {demande.photo_url ? (
        <img
          className="validation__photo"
          src={demande.photo_url}
          alt={`Le travail de ${demande.child_name}`}
        />
      ) : (
        <p className="muted small">
          Aucune photo n'a été envoyée avec ce travail : il est sur le cahier.
        </p>
      )}

      {demande.imprimable ? (
        <LiensDImpression
          cible={{ quoi: "exercice", childId: demande.child_id, exerciseId: demande.exercise_id }}
          corrige={corriges.length > 0}
        />
      ) : null}

      {pages ? (
        corriges.length ? (
          <details className="validation__corrige">
            <summary>Voir le corrigé de l'ouvrage</summary>
            {corriges.map((url, rang) => (
              <img
                key={url}
                className="validation__photo"
                src={url}
                alt={`Corrigé, page ${rang + 1}`}
              />
            ))}
          </details>
        ) : dUnMot && demande.solution ? (
          <details className="validation__corrige">
            <summary>Voir le corrigé</summary>
            <TexteMathematique className="debrief__texte">{demande.solution}</TexteMathematique>
          </details>
        ) : null
      ) : (
        <details className="validation__corrige">
          <summary>Voir le corrigé</summary>
          <TexteMathematique className="debrief__texte">{demande.solution}</TexteMathematique>
        </details>
      )}

      {dUnMot ? (
        <VerdictDUnMot
          demande={demande}
          arme={arme}
          setArme={setArme}
          envoi={envoi}
          juger={juger}
          erreur={erreur}
        />
      ) : (
        <>
          <h3 className="card__title">{marquee ? "Cochez les réponses justes" : "À vérifier"}</h3>
          {demande.checklist.map((point, index) => (
            <label key={point} className="check">
              <input
                type="checkbox"
                checked={coches[index] ?? false}
                disabled={envoi}
                onChange={(event) =>
                  setCoches((precedent) =>
                    precedent.map((valeur, rang) =>
                      rang === index ? event.target.checked : valeur,
                    ),
                  )
                }
              />
              <span>
                {point}
                <PhraseDuTuteur proposition={propositions[String(index)]} />
              </span>
            </label>
          ))}

          {/* **Un seul bouton, et il n'a pas de contraire.** Le parent ne juge pas :
          il coche, et `exercise.validate_by_parent` décide. Le bouton reste
          actif quand la grille est incomplète — il dit alors ce qui manque,
          plutôt que d'être un bouton mort qu'on croit en panne — mais l'envoi
          n'est possible qu'une fois tout regardé : une grille partielle ne dit
          pas « faux », elle ne dit rien. */}
          <Button disabled={!envoyable || envoi} onClick={() => void valider()}>
            {envoi
              ? "Envoi…"
              : marquee
                ? libelleDuBoutonMarque(coches)
                : libelleDuBouton(coches, demande.checklist.length)}
          </Button>
          {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
        </>
      )}
    </Card>
  );
}

/**
 * Correct ou pas correct, avec les critères **en lecture seule** au-dessus.
 *
 * Les critères ne se cochent pas : sur une composition, « problématique
 * posée » est à moitié présente bien plus souvent que présente ou absente, et
 * une case ferait croire que le verdict se calcule. Ils disent ce qu'il faut
 * regarder ; la réponse reste un mot.
 *
 * Le verdict ne se reprend pas — il écrit une observation dans `mastery`, un
 * journal en ajout seul —, d'où la confirmation **dans la page** (pas de
 * `window.confirm`, refusé en silence par la coquille Android).
 */
function VerdictDUnMot({
  demande,
  arme,
  setArme,
  envoi,
  juger,
  erreur,
}: {
  demande: ValidationADemander;
  arme: boolean | null;
  setArme: (valeur: boolean | null) => void;
  envoi: boolean;
  juger: (correct: boolean) => Promise<void>;
  erreur: string | null;
}) {
  const criteres = demande.criteres ?? [];
  return (
    <>
      {criteres.length ? (
        <>
          <h3 className="card__title">Ce qu'il faut regarder</h3>
          <ul className="list">
            {criteres.map((critere) => (
              <li key={critere.libelle}>
                <strong>{critere.libelle}</strong>
                {critere.attendu ? <span className="muted"> — {critere.attendu}</span> : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <h3 className="card__title">Est-ce correct ?</h3>
      <PhraseDuTuteur proposition={propositionsDe(demande).copie} />
      {arme === null ? (
        <div className="list__actions">
          <Button disabled={envoi} onClick={() => setArme(true)}>
            Correct
          </Button>
          <Button variant="secondary" disabled={envoi} onClick={() => setArme(false)}>
            Pas correct
          </Button>
        </div>
      ) : (
        <>
          <p className="muted small">{libelleDeConfirmation(arme, demande.child_name)}</p>
          <div className="list__actions">
            <Button disabled={envoi} onClick={() => void juger(arme)}>
              {envoi ? "Envoi…" : "Confirmer"}
            </Button>
            <Button variant="quiet" disabled={envoi} onClick={() => setArme(null)}>
              Annuler
            </Button>
          </div>
        </>
      )}
      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
    </>
  );
}
